import { useLocale } from 'hooks/use-locale.hook';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ACTION_STATUSES, PATCH_STATUSES } from 'shared/consts';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { selectCart } from 'store/cart/selectors';
import { patchTransaction } from 'store/transactions/actions';
import { selectPatchTransactionsStatus, selectTypeOfPatchTransaction } from 'store/transactions/selectors';
import { formatDate } from 'utils/formatDate';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { TransactionDetails, PatchTransaction } from 'store/transactions/types';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { searchParams } from 'pages/date-select-page/interface';
import { goBack } from 'store/router/actions';

export interface UseCancelBookingReturnValues {
  onBack: VoidFunction;
  onCancelButtonClick: VoidFunction;
  formattedDate: { [arg: string]: string } | null;
  vendorName: string;
  showMiniappPaymentsNavigation: boolean;
  isMobileDevice: boolean;
  patchTransactionData: Pick<PatchTransaction, 'status' | 'type'>;
}

interface UseCancelBookingProps {
  transaction?: TransactionDetails;
  setIsCancelBookingStep?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useCancelBooking = ({
  transaction,
  setIsCancelBookingStep,
}: UseCancelBookingProps): UseCancelBookingReturnValues => {
  const locale = useLocale();
  const dispatch = useDispatch();
  const { showMiniappPaymentsNavigation } = useFlags();
  const { shouldOpenCancelStep } = useSearchParams<searchParams>();
  const isMobileDevice = useIsSmallViewportWidth();
  const buildingTimeZone = useSelector(selectBuildingTimeZone);
  const cart = useSelector(selectCart);
  const startAt = cart ? cart?.items[0].resource_booking?.start_at : transaction?.details?.start_at;
  const formattedDate = startAt ? formatDate(startAt, locale as string, buildingTimeZone) : null;
  const patchTransactionStatus = useSelector(selectPatchTransactionsStatus);
  const typeOfPatchTransaction = useSelector(selectTypeOfPatchTransaction);
  const [cancellationPending, setCancellationPending] = useState(false);

  const patchTransactionData = {
    status: patchTransactionStatus,
    type: typeOfPatchTransaction,
  };
  const vendorName = cart ? cart?.items[0].display_info.title : transaction?.details?.name;

  const cancelStatus = PATCH_STATUSES.CANCELLED;

  const onCancelButtonClick = useCallback(() => {
    dispatch(
      patchTransaction.request({
        transactionId: cart ? cart?.transaction_id?.toString() : transaction?.id?.toString(),
        status: cancelStatus,
      }),
    );
    setCancellationPending(true);
  }, [dispatch, cart, transaction?.id, cancelStatus, setCancellationPending]);

  const deleteCancelStep = useCallback(() => {
    setCancellationPending(false);
    setIsCancelBookingStep(false);
  }, [setIsCancelBookingStep]);

  const onBack = useCallback(() => {
    if (shouldOpenCancelStep) {
      dispatch(goBack());
    } else {
      deleteCancelStep();
    }
  }, [shouldOpenCancelStep, dispatch, deleteCancelStep]);

  useEffect(() => {
    if (cancellationPending && patchTransactionStatus === ACTION_STATUSES.FULFILLED) {
      deleteCancelStep();
    }
  }, [patchTransactionStatus, cancellationPending, deleteCancelStep]);

  return {
    onBack,
    onCancelButtonClick,
    formattedDate,
    vendorName,
    showMiniappPaymentsNavigation,
    isMobileDevice,
    patchTransactionData,
  };
};
